import React from 'react'

import styled from 'styled-components'
import { createGlobalStyle } from 'styled-components'

import { FooterMeta } from 'src/components/blocks/Footer'
import { G2Banner } from 'src/components/blocks/G2Banner'
import { LogostripSection } from 'src/components/blocks/Logostrip'
import { MinimalHeader } from 'src/components/blocks/MinimalHeader'
import { QuoteCardNew } from 'src/components/blocks/QuoteCard'
import { Body3, TitleH2, TitleH4 } from 'src/components/blocks/Typography'
import { Container } from 'src/components/partials/Container'
import FaIcon from 'src/components/partials/FaIcon'
import { Grid, Column } from 'src/components/partials/Grid'
import Layout from 'src/components/partials/Layout'
import MediaObject from 'src/components/partials/MediaObject'
import { ResponsiveSectionWrapper } from 'src/components/partials/Section'
import SEO from 'src/components/partials/Seo'
import SignUpForm from 'src/components/partials/SignUpForm'
import UIList from 'src/components/partials/UIList'
import { flowQuote } from 'src/data/quotes'
import { Breakpoints } from 'src/utilities/Breakpoints'
import { BrandColors, BrandTokens } from 'src/utilities/Colors'

const PageBgColor = createGlobalStyle`
  body {
    background-color: ${BrandColors.white};
  }
`

const HeaderSectionWrapper = styled(ResponsiveSectionWrapper)`
  ul {
    li {
      max-width: 55ch;
    }
  }
`

// const HeaderBgGradient = styled.div`
//   position: absolute;
//   z-index: 1;
//   top: 0;
//   left: 0;
//   width: 100%;
//   height: 100%;
// `

const HeaderContainer = styled(Container)`
  position: relative;
  z-index: 2;
  em {
    font-family: inherit;
    font-style: inherit;
  }

  /* Reorder <Grid> columns on mobile. making the first column appear last */
  @media (max-width: ${Breakpoints.md}px) {
    ${Grid} {
      display: flex;
      flex-direction: column-reverse;
    }
  }
`

const uiListItems = [
  {
    title: 'API-first, event-driven architecture',
    content:
      'A rich library of developer-friendly APIs for data file import provides you with unparalleled control and extensibility beyond conventional plug-and-play tools.',
    icon: <FaIcon classes='fa-sharp fa-light fa-sitemap' size='1.5rem' />,
    iconBgColor: BrandColors.pigeon200
  },
  {
    title: 'Highly intuitive data import experience designed for end users',
    content:
      'Fully customizable workflows and styling to align with your brand and a simple workbook-style, AI-assisted user experience.',
    icon: <FaIcon classes='fa-sharp fa-light fa-file-import' size='1.5rem' />,
    iconBgColor: BrandColors.pigeon200
  },
  {
    title: 'Optimize data quality and efficiency',
    content:
      'Reduce data errors and increase quality and usability while reducing the costs of manual data cleanup and development.',
    icon: (
      <FaIcon classes='fa-sharp fa-light fa-arrow-progress' size='1.5rem' />
    ),
    iconBgColor: BrandColors.pigeon200
  }
]

const HeaderSection = () => {
  return (
    <HeaderSectionWrapper
      backgroundColor={BrandTokens.backgroundLight}
      paddingLarge='3rem 0 6rem'
      paddingSmall='0 0 3rem'
    >
      <HeaderContainer>
        <Grid columns={{ md: 12 }}>
          <Column span={{ md: 7 }}>
            <TitleH2 as='h1' marginBottom='3rem' marginTop='2rem'>
              Get started with the Flatfile Data Exchange Platform
            </TitleH2>

            <UIList gap='1.5rem'>
              {uiListItems.map((item, index) => (
                <li key={index}>
                  <MediaObject
                    content={<Body3>{item.content}</Body3>}
                    image={item.icon}
                    imageBgColor={item.iconBgColor}
                    title={<TitleH4 as='h3'>{item.title}</TitleH4>}
                    size='3rem'
                  />
                </li>
              ))}
            </UIList>
          </Column>
          <Column span={{ md: 5 }}>
            <SignUpForm
              fillUtms={true}
              hsFormId='0b7f7431-c9e8-4484-8fe4-190e06fd24e7'
              hsSource='account/sign-up'
              theme='light'
              // formTitle=''
              formMinHeight='525px'
            />
          </Column>
        </Grid>
      </HeaderContainer>

      {/* <HeaderBgGradient /> */}
    </HeaderSectionWrapper>
  )
}

const AccountSignUpPage = () => (
  <Layout>
    <PageBgColor />

    <MinimalHeader />
    <HeaderSection />
    <LogostripSection showTopHat paddingLarge='5rem 0' paddingSmall='3rem 0' />
    <QuoteCardNew quote={flowQuote} />
    <G2Banner backgroundColor={BrandColors.black} showCta={false} />
    <FooterMeta
      backgroundColor={BrandTokens.backgroundLight}
      textColor={BrandTokens.textDark90}
    />
  </Layout>
)

export default AccountSignUpPage

export const Head = () => (
  <SEO
    description={
      'Build the ideal data file import experience with Flatfile and turn csv, xls, xlsx, pdf and more into API-ready data. Sign up and start building.'
    }
    title='Sign up for Flatfile | Smart APIs for file-based data import'
  />
)
